// Funciones auxiliares para estudios
import dayjs from "dayjs";

export const getTipoArchivo = (nombreArchivo) => {
  if (!nombreArchivo) return null;
  let partes = nombreArchivo.split(".");
  let tipo = partes.at(-1)?.toLowerCase();
  if (tipo && ["pdf", "jpg", "jpeg", "bmp", "png", "gif", "rtf"].includes(tipo)) return tipo;
  else return null;
};

export const armarConjuntosArchivos = (estudios, setConjuntos) => {
  let conjuntosTmp = [];
  estudios.forEach((estudio) => {
    if (estudio.fecha !== "no disponible") {
      let fechaTmp = dayjs(estudio.fechaModificacion).format("DD/MM/YYYY");
      let indiceConjuntoExistente = conjuntosTmp.findIndex(
        (conjunto) => conjunto.fecha === fechaTmp
      );
      if (indiceConjuntoExistente !== -1) {
        conjuntosTmp[indiceConjuntoExistente].estudios.push(estudio);
      } else {
        conjuntosTmp.push({
          fecha: fechaTmp,
          fechaJS: dayjs(estudio.fechaModificacion),
          estudios: [{ ...estudio }],
        });
      }
    }
  });
  conjuntosTmp.sort(
    (conjuntoA, conjuntoB) => conjuntoB.fechaJS.unix() - conjuntoA.fechaJS.unix()
  );
  setConjuntos(conjuntosTmp);
};

// Función para convertir contenido RTF a HTML y luego a React components
export const parseRtfToHtml = (rtfContent) => {
  if (!rtfContent) return null;

  try {
    // Importación dinámica para evitar problemas si no está instalado
    const parse = require("html-react-parser").default || require("html-react-parser");
    const { convertRtfToHtml } = require("rtf-to-html-converter");

    // Función para limpiar símbolos ? después de letras con acento
    const cleanQuestionMarks = (text) => {
      if (!text) return text;

      let cleanedText = text;

      // 1. Eliminar ? que aparece después de cualquier letra con acento
      cleanedText = cleanedText.replace(
        /([áéíóúàèìòùâêîôûäëïöüñçÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÄËÏÖÜÑÇ])\?/g,
        "$1"
      );

      // 2. Convertir ? restantes (que no están después de acentos) en saltos de línea
      cleanedText = cleanedText.replace(/\?\s*\?\s*\?+/g, "<br/>"); // 3 o más ? seguidos
      cleanedText = cleanedText.replace(/\?\s*\?/g, "<br/>"); // 2 ? seguidos
      cleanedText = cleanedText.replace(/\s*\?\s*/g, "<br/>"); // ? individual con espacios

      // 3. Limpiar múltiples <br/> seguidos y dejar máximo 2
      cleanedText = cleanedText.replace(/(<br\/>){3,}/g, "<br/><br/>");

      // 4. Limpiar espacios al inicio y final de líneas
      cleanedText = cleanedText.replace(/<br\/>\s+/g, "<br/>");
      cleanedText = cleanedText.replace(/\s+<br\/>/g, "<br/>");

      return cleanedText;
    };

    // Limpiar el contenido RTF antes de convertir
    const cleanedRtfContent = cleanQuestionMarks(rtfContent);

    // Primero convertir RTF a HTML
    const htmlContent = convertRtfToHtml(cleanedRtfContent);

    if (!htmlContent) {
      console.warn("No se pudo convertir RTF a HTML");
      const cleanedFallback = cleanQuestionMarks(rtfContent);
      return (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial" }}>
          {cleanedFallback}
        </pre>
      );
    }

    // Limpiar también el HTML resultante
    const cleanedHtmlContent = cleanQuestionMarks(htmlContent);

    // Luego parsear el HTML a React components
    const bodyRegex = /<body[^>]*>((.|[\n\r])*)<\/body>/im;
    const bodyMatches = cleanedHtmlContent.match(bodyRegex);

    if (bodyMatches && bodyMatches.length > 1) {
      const bodyContent = bodyMatches[1];
      return parse(bodyContent);
    } else {
      return parse(cleanedHtmlContent);
    }
  } catch (error) {
    console.error("Error converting RTF to HTML:", error);
    // Fallback: mostrar RTF como texto plano con formato básico
    let cleanedFallback = rtfContent;
    cleanedFallback = cleanedFallback.replace(
      /([áéíóúàèìòùâêîôûäëïöüñçÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÄËÏÖÜÑÇ])\?/g,
      "$1"
    );
    cleanedFallback = cleanedFallback.replace(/\?\s*\?\s*\?+/g, "<br/>");
    cleanedFallback = cleanedFallback.replace(/\?\s*\?/g, "<br/>");
    cleanedFallback = cleanedFallback.replace(/\s*\?\s*/g, "<br/>");
    cleanedFallback = cleanedFallback.replace(/(<br\/>){3,}/g, "<br/><br/>");
    cleanedFallback = cleanedFallback.replace(/<br\/>\s+/g, "<br/>");
    cleanedFallback = cleanedFallback.replace(/\s+<br\/>/g, "<br/>");

    return (
      <div style={{ fontFamily: "Arial, sans-serif", whiteSpace: "normal" }}>
        <div dangerouslySetInnerHTML={{ __html: cleanedFallback }} />
      </div>
    );
  }
};

// Función para abrir contenido en nueva ventana/pestaña
export const abrirEnNuevaVentana = (src, tipo, titulo) => {
  if (tipo === "pdf") {
    // Para PDFs, crear una página HTML que use object/embed para mejor compatibilidad
    const ventana = window.open("", "_blank");
    if (ventana) {
      ventana.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${titulo || "Documento PDF"}</title>
            <meta charset="UTF-8">
            <style>
              body { 
                margin: 0; 
                padding: 0; 
                height: 100vh; 
                overflow: hidden;
                background: #333;
              }
              .pdf-container { 
                width: 100%; 
                height: 100vh; 
                display: flex;
                flex-direction: column;
                background: #333;
              }
              .pdf-header {
                background: #2c2c2c;
                color: white;
                padding: 10px 20px;
                text-align: center;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 14px;
                border-bottom: 1px solid #555;
              }
              .pdf-viewer { 
                flex: 1;
                border: none;
                width: 100%;
                background: white;
              }
              .loading {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 200px;
                color: white;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              }
              .fallback {
                text-align: center;
                padding: 40px 20px;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                background: white;
                margin: 20px;
                border-radius: 8px;
              }
              .fallback h3 {
                color: #333;
                margin-bottom: 20px;
              }
              .fallback p {
                color: #666;
                margin: 10px 0;
              }
              .download-btn {
                display: inline-block;
                background: #007bff;
                color: white;
                padding: 10px 20px;
                text-decoration: none;
                border-radius: 5px;
                margin-top: 15px;
                font-weight: bold;
                transition: background 0.3s;
              }
              .download-btn:hover {
                background: #0056b3;
              }
            </style>
          </head>
          <body>
            <div class="pdf-container">
              <div class="pdf-header">${titulo || "Documento PDF"}</div>
              <div class="loading" id="loading">Cargando PDF...</div>
              <object 
                class="pdf-viewer" 
                data="${src}" 
                type="application/pdf"
                style="display: none;"
                onload="document.getElementById('loading').style.display='none'; this.style.display='block';">
                <embed 
                  src="${src}" 
                  type="application/pdf" 
                  width="100%" 
                  height="100%" />
                <div class="fallback">
                  <h3>No se puede mostrar el PDF</h3>
                  <p>Su navegador no soporta la visualización de PDFs integrada.</p>
                  <p>Puede descargar el archivo para verlo en una aplicación externa.</p>
                  <a class="download-btn" href="${src}" download="${
        titulo || "documento.pdf"
      }">Descargar PDF</a>
                </div>
              </object>
            </div>
            <script>
              // Fallback si el objeto no carga después de 5 segundos
              setTimeout(function() {
                var loading = document.getElementById('loading');
                var object = document.querySelector('.pdf-viewer');
                if (loading.style.display !== 'none') {
                  loading.style.display = 'none';
                  object.style.display = 'block';
                }
              }, 5000);
            </script>
          </body>
        </html>
      `);
      ventana.document.close();
    }
  } else {
    // Para otros tipos, intentar abrir directamente
    window.open(src, "_blank");
  }
};
